import { EventCategory, EventCategoryIconMap, EventCategoryMap, FrequencyPluralMap } from "@qendo/calendar";
import { OptionType } from "@qendo/types";
import { ucfirst } from "@samlevy/string-helpers";
import { endOfDay, isSameDay, isSameMinute, startOfDay } from "date-fns";
import { useGlobalSearchParams } from "expo-router";
import { useMemo, useState } from "react";
import { View } from "react-native";
import { Button } from "~/components/Button";
import { DataList } from "~/components/layout/DataList";
import { DataListItem } from "~/components/layout/DataListItem";
import { BasicGallery } from "~/components/layout/gallery/BasicGallery";
import { ConfirmationModal } from "~/components/layout/modal/ConfirmationModal";
import { FullPageModal } from "~/components/layout/modal/FullPageModal";
import { Title } from "~/components/layout/text";
import { Paragraphs } from "~/components/layout/text/Paragraphs";
import { Authorize } from "~/components/permission/Authorize";
import { Dismiss } from "~/components/permission/Dismiss";
import { Only } from "~/components/permission/Only";
import { QendoIcon } from "~/components/QendoIcon";
import { Flex, Gap, Items } from "~/constants/layout";
import { useDismiss } from "~/providers/DismissProvider";
import { useFeedback } from "~/providers/FeedbackProvider";
import { useJournal } from "~/providers/JournalProvider";
import { useOptions } from "~/providers/OptionsProvider";
import { filled } from "~/util";
import {
    currentTimezone,
    formatDateForHumans,
    formatTimeForHumans,
    formatTimezoneName,
    fromTimezone,
    timezonesMatch,
} from "~/util/dates";
import { getEndDate } from "~/util/qendo/calendar";

export default function JournalDetails() {
    const { id }: { id: string } = useGlobalSearchParams();
    const { handleDismiss } = useDismiss();
    const { findOption } = useOptions();
    const { findEvent, canUpdateCalendar, destroyEvent } = useJournal();
    const [confirm, setConfirm] = useState(false);
    const { addToast, saving, setSaving } = useFeedback();

    const event = useMemo(() => findEvent(id), [id, findEvent]);
    const real_event = useMemo(() => (event?.primary_id && findEvent(event.primary_id)) || event, [event, findEvent]);

    const option = useMemo(
        () => findOption(OptionType.calendar_events, real_event?.option_id),
        [findOption, real_event?.option_id],
    );
    const current_timezone = currentTimezone();
    const time_zone = event?.time_zone ?? real_event?.time_zone ?? current_timezone;
    const is_same_timezone = useMemo(() => timezonesMatch(current_timezone, time_zone), [current_timezone, time_zone]);

    if (!event || !real_event) {
        return <Dismiss />;
    }

    const event_start = fromTimezone(event.start_at, time_zone);
    const event_end = filled(event.end_at) ? fromTimezone(event.end_at, time_zone) : null;

    const start_at = isSameMinute(event_start, startOfDay(event_start)) ? "Midnight" : formatTimeForHumans(event_start);
    const end_at =
        (event.all_day && "All day") ||
        (event_end && isSameMinute(event_end, endOfDay(event_end)) && "Midnight") ||
        (event_end && formatTimeForHumans(event_end)) ||
        null;
    const start_day = formatDateForHumans(real_event.start_at, "locale");
    const last_day = getEndDate(real_event);
    const end_day = last_day && !isSameDay(start_day, last_day) ? formatDateForHumans(last_day, "locale") : false;

    const event_type = option?.group ?? EventCategory.appointment;
    const title = option?.title ?? EventCategoryMap[event_type];
    const icon = EventCategoryIconMap[event_type];
    const event_timing = event.all_day
        ? `All day, starting at ${start_at}`
        : [start_at, end_at].filter(filled).join(" - ");

    const can_update = Boolean(canUpdateCalendar());
    const can_delete = Boolean(can_update && !saving);

    const doDelete = () => {
        if (!can_delete) {
            return;
        }

        setSaving(true);

        destroyEvent(real_event.id)
            .then(() => {
                addToast({
                    icon: "checkmark-circle",
                    content: "Event deleted",
                    duration: 3000,
                });
            })
            .finally(() => setSaving(false));
    };

    return (
        <Authorize ability="view" section="calendar">
            <FullPageModal
                asRoute
                title="Event details"
                headerLeft={() => (
                    <Only when={can_update}>
                        <Button
                            variant="quiet"
                            title="Edit"
                            href={{
                                pathname: "/calendar/[id]/edit",
                                params: { id },
                            }}
                            size="sm"
                        />
                    </Only>
                )}
                withDone
                onRequestClose={handleDismiss}
                footer={() => (
                    <Only when={can_update}>
                        <Button
                            icon="trash"
                            title="Delete event"
                            disabled={!can_delete}
                            variant="destructive"
                            onPress={() => setConfirm(true)}
                        />
                        <Only
                            when={confirm}
                            show={() => (
                                <ConfirmationModal
                                    onConfirm={doDelete}
                                    title="Are you sure you want to delete this event?"
                                    confirmText="Delete event"
                                    confirmVariant="destructive-quiet"
                                    loading={filled(saving)}
                                    onRequestClose={() => setConfirm(false)}
                                />
                            )}
                        />
                    </Only>
                )}
            >
                <View style={[Gap.sm, Flex.row, Items.center]}>
                    <QendoIcon name={icon} size={24} />
                    <Title size="xl">{title}</Title>
                </View>
                <DataList>
                    <DataListItem title="Title" data={real_event.title} />
                    {filled(real_event.location) && <DataListItem title="Location" data={real_event.location} />}
                    <DataListItem title="Time" data={event_timing} />
                    {!is_same_timezone && <DataListItem title="Time zone" data={formatTimezoneName(time_zone)} />}
                    {filled(end_day) && <DataListItem title="Dates" data={`${start_day} - ${end_day}`} />}
                    {filled(real_event.notes) && (
                        <DataListItem title="Notes">
                            <Paragraphs content={real_event.notes} />
                        </DataListItem>
                    )}
                </DataList>
                {filled(real_event.recurrence) && (
                    <DataList>
                        <DataListItem title="Repeats">
                            {real_event.recurrence.interval === 1
                                ? ucfirst(real_event.recurrence.frequency)
                                : `Every ${real_event.recurrence.interval} ${FrequencyPluralMap[real_event.recurrence.frequency]}`}
                        </DataListItem>
                        {filled(real_event.recurrence.occurrence) && (
                            <DataListItem title="Times" data={real_event.recurrence.occurrence} />
                        )}
                        {filled(real_event.recurrence.end_date) && (
                            <DataListItem
                                title="Until"
                                data={formatDateForHumans(real_event.recurrence.end_date, "locale")}
                            />
                        )}
                    </DataList>
                )}
                {filled(real_event.photograph_ids) && <BasicGallery images={real_event.photograph_ids} />}
            </FullPageModal>
        </Authorize>
    );
}
